import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled as muiStyled } from "@mui/material/styles";
import type { Result } from "hooks/utils/material-data";
import type { FC } from "react";

export interface IndustryProps {
  grade: Result | undefined;
}

const MaterialBox = muiStyled(Box)(() => ({
  overflowY: "auto",
  maxHeight: "82px",
  minHeight: "22px",
  width: "100%",
  marginTop: "12px",
  backgroundColor: "#f2f2f2",
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-track": {
    boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
    webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,.1)",
    outline: "0px solid rgba(0,0,0,0.4)",
    borderRadius: "8px",
  },
}));

const SpanItem = styled.span({
  minHeight: "22px",
  fontSize: "var(--body-b3-12-regular-size, 12px)",
  fontWeight: 400,
  padding: "2px 8px",
  backgroundColor: "#e6e6e6",
  borderRadius: "16px",
  maxWidth: "98%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "inline-block",
  marginRight: "8px",
});

export function createGradeRegex(grade: string): RegExp {
  const escapedGrade = grade.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const suffixPattern = "(?:CN|RE|[0-9][EP])";
  const numberPattern = "[0-9]+";
  const whitespace = "\\s+";

  const pattern =
    `^${escapedGrade}$|` +
    `^${escapedGrade}${whitespace}` +
    `(?!MAS|BBS|MBS)` +
    `(?:` +
    `(?:${suffixPattern}|${numberPattern}(?:${suffixPattern}))` +
    `(?:${whitespace}(?:${suffixPattern}|${numberPattern}(?:${suffixPattern})?|${numberPattern}))*` +
    `|` +
    `${numberPattern}${whitespace}${suffixPattern}` +
    `(?:${whitespace}(?:${suffixPattern}|${numberPattern}(?:${suffixPattern})?|${numberPattern}))*` +
    `)$`;

  return new RegExp(pattern, "i");
}

export const Industry: FC<IndustryProps> = ({ grade }) => {
  return (
    <Grid
      container
      spacing={1}
      sx={{ "& .MuiGrid-item": { paddingLeft: 0, paddingRight: "8px" } }}
    >
      <Grid key="industry" size={12}>
        <Box
          sx={{
            minHeight: "48px",
            padding: "16px",
            width: "100%",
            backgroundColor: "#f2f2f2",
          }}
        >
          <Typography variant="label" component="div">
            Industry
          </Typography>
          {grade && grade.industry.length > 0 ? (
            <MaterialBox>
              {grade.industry.map((text) => (
                <SpanItem title={text} key={text}>
                  {text}
                </SpanItem>
              ))}
            </MaterialBox>
          ) : (
            <Typography variant="label" component="span">
              -
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid key="applicationClass" size={12}>
        <Box
          sx={{
            minHeight: "48px",
            padding: "16px",
            width: "100%",
            backgroundColor: "#f2f2f2",
          }}
        >
          <Typography variant="label" component="div">
            Application Class
          </Typography>
          {grade && grade.class.length > 0 ? (
            <MaterialBox>
              {grade.class.map((text) => (
                <SpanItem title={text} key={text}>
                  {text}
                </SpanItem>
              ))}
            </MaterialBox>
          ) : (
            <Typography variant="label" component="span">
              -
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid key="application" size={12}>
        <Box
          sx={{
            minHeight: "48px",
            padding: "16px",
            width: "100%",
            backgroundColor: "#f2f2f2",
          }}
        >
          <Typography variant="label" component="div">
            Application
          </Typography>
          {grade && grade.application.length > 0 ? (
            <MaterialBox>
              {grade.application.map((text) => (
                <SpanItem title={text} key={text}>
                  {text}
                </SpanItem>
              ))}
            </MaterialBox>
          ) : (
            <Typography variant="label" component="span">
              -
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
